<script setup>
import { computed, useAttrs } from 'vue';

import { useTheme } from '../composables/useTheme';

/**
 * Calendar for picking a date range.
 *
 * Keeps the model stable: Quasar's q-date collapses a same-day selection into a plain date string,
 * while this primitive always emits a `{ from, to }` object (a same-day pick becomes `{ from: d, to: d }`).
 *
 * @status recommended
 * @group forms-input
 * @useWhen Selecting a start/end date range from a visual calendar
 * @related HrDate
 * @related HrTimeField
 * @a11y Calendar days are keyboard-focusable: Arrow keys move between days, Enter/Space selects. The surrounding
 *   context (e.g. dialog title or a labeled field) should name the picker, as the calendar has no intrinsic
 *   accessible label.
 * @see https://quasar.dev/vue-components/date Quasar q-date API
 */
defineOptions({ name: 'HrDateRange', inheritAttrs: false });

const props = defineProps({
  /**
   * Selected range as `{ from, to }` with 'YYYY/MM/DD' strings.
   * @example {"from":"2026/04/06","to":"2026/04/17"}
   */
  modelValue: {
    type: Object,
    default: null,
  },
  /**
   * Predicate controlling which days are selectable; receives a 'YYYY/MM/DD' string and returns a boolean.
   */
  options: {
    type: Function,
    default: null,
  },
  /**
   * Month/year to focus initially when modelValue is empty, as 'YYYY/MM'. Ignored once a value is set.
   * @example 2026/04
   */
  defaultYearMonth: {
    type: String,
    default: null,
  },
  /** Prevents interaction and dims the calendar. */
  disable: {
    type: Boolean,
    default: false,
  },
  /**
   * Legacy props for backwards compatibility with the legacy theme.
   * @deprecated Only for backwards compatibility — remove once legacy consumers are migrated.
   */
  legacyProps: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits({
  /** Emitted when the selected range changes; always a `{ from, to }` object or null. */
  'update:modelValue': null,
});

const attrs = useAttrs();
const { hasTheme } = useTheme();

const classes = ['hr-date-range'];

const effectiveProps = computed(() => {
  const base = {
    modelValue: props.modelValue,
    range: true,
    ...(props.options !== null && { options: props.options }),
    ...(props.defaultYearMonth !== null && { defaultYearMonth: props.defaultYearMonth }),
    disable: props.disable,
  };

  if (hasTheme.value) {
    return { ...base, flat: true, ...attrs };
  }
  return { ...base, ...props.legacyProps, ...attrs };
});

/**
 * q-date emits a plain date string when both range ends land on the same day; normalize it back
 * to `{ from, to }` so consumers always see one model shape.
 *
 * @param {string|{from: string, to: string}|null} value
 * @returns {void}
 */
function onUpdateModelValue(value) {
  emit('update:modelValue', typeof value === 'string' ? { from: value, to: value } : value);
}
</script>

<template>
  <q-date
    v-bind="effectiveProps"
    :class="hasTheme ? classes : undefined"
    @update:model-value="onUpdateModelValue"
  />
</template>

<style lang="scss" scoped>

/* Only applies to light/dark themes */

.hr-date-range {
  box-shadow: none;
  border-radius: var(--app-shape-corner-medium);
  background: var(--app-surface-container-high);
  color: var(--app-on-surface);
}

/*
 * Quasar hardcodes #fff text on this header (assumes a dark primary); the M3 dark theme's
 * primary is light, so the on-primary token (dark text) is used instead for correct contrast.
 */
.hr-date-range :deep(.q-date__header) {
  background-color: var(--app-primary);
  color: var(--app-on-primary);
}

/*
 * Range endpoints render via QBtn's color/text-color props, which resolve to
 * Quasar's own !important utility classes (.bg-primary/.text-white) — matched here.
 */
.hr-date-range :deep(.q-date__calendar-item .bg-primary) {
  background: var(--app-primary) !important;
}

.hr-date-range :deep(.q-date__calendar-item .text-white) {
  color: var(--app-on-primary) !important;
}

.hr-date-range :deep(.q-date__range::before),
.hr-date-range :deep(.q-date__range-from::before),
.hr-date-range :deep(.q-date__range-to::before) {
  background-color: var(--app-primary);
  opacity: 0.16;
}

.hr-date-range :deep(.q-date__today) {
  box-shadow: 0 0 0 1px var(--app-primary);
}

</style>
